/**
 * ============================================================================
 * PeakHoursSection - PEAK HOURS DISPLAY WITH HIGHLIGHT
 * ============================================================================
 *
 * ⚠️  CRITICAL WARNINGS FOR FUTURE CLAUDE SESSIONS:
 *
 * 1. PEAK HOUR DETECTION
 *    - Find hour with maximum revenue
 *    - Highlight with blue background and 🔥 emoji
 *    - Only one peak hour (the highest)
 *
 * 2. SPORT FILTERING
 *    - Parent manages selectedSportId state
 *    - When sport changes, parent refetches with usePeakHours
 *    - This component just displays filtered results
 *
 * 3. LOADING STATE
 *    - Optional isLoading prop
 *    - Show spinner while fetching new sport data
 *    - Keep previous data visible (TanStack Query placeholderData)
 *
 * 4. EMPTY STATE
 *    - Show when no peak hours data
 *    - Friendly message with icon
 *
 * ============================================================================
 */

import React, { useMemo } from 'react';
import { Clock } from 'lucide-react';
import { formatCurrency } from '../../utils/revenueFormatters';
import type { PeakHour } from '../../types/revenue';

interface PeakHoursSectionProps {
  peakHours: PeakHour[];
  availableSports: Array<{ sport_id: number; sport_name: string }>;
  selectedSportId: number | null;
  onSportChange: (sportId: number | null) => void;
  isLoading?: boolean;
}

/**
 * Peak hours section with sport filter
 */
export function PeakHoursSection({
  peakHours,
  availableSports,
  selectedSportId,
  onSportChange,
  isLoading = false,
}: PeakHoursSectionProps) {
  // Find peak hour (hour with max revenue)
  const maxRevenue = useMemo(() => {
    if (!peakHours || peakHours.length === 0) return 0;
    return Math.max(...peakHours.map((h) => h.revenue));
  }, [peakHours]);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-bold text-gray-900">Peak Hours</h3>

        {/* Sport Filter Dropdown */}
        <select
          value={selectedSportId || 'all'}
          onChange={(e) =>
            onSportChange(e.target.value === 'all' ? null : Number(e.target.value))
          }
          className="px-4 py-2 border border-gray-200 rounded-xl text-sm font-semibold outline-none focus:border-blue-500 bg-white"
          disabled={isLoading}
        >
          <option value="all">All Sports</option>
          {availableSports.map((sport) => (
            <option key={sport.sport_id} value={sport.sport_id}>
              {sport.sport_name}
            </option>
          ))}
        </select>
      </div>

      {/* Content */}
      <div className="space-y-3">
        {isLoading ? (
          // Loading state
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : peakHours && peakHours.length > 0 ? (
          // Peak hours list
          peakHours.map((slot, idx) => {
            const isPeak = slot.revenue === maxRevenue && slot.revenue > 0;

            return (
              <div
                key={idx}
                className={`flex items-center justify-between p-3 rounded-xl transition-all ${
                  isPeak ? 'bg-blue-50 border-2 border-blue-200' : 'bg-gray-50'
                }`}
              >
                <div>
                  <p className="font-semibold text-gray-900 text-sm">{slot.time}</p>
                  <p className="text-gray-500 text-xs">
                    {slot.bookings} booking{slot.bookings !== 1 ? 's' : ''}
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-gray-900">{formatCurrency(slot.revenue)}</p>
                  {isPeak && <span className="text-xs font-bold text-blue-600">🔥 Peak</span>}
                </div>
              </div>
            );
          })
        ) : (
          // Empty state
          <div className="text-center py-8">
            <Clock className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-600">No booking data yet</p>
            <p className="text-sm text-gray-500 mt-1">
              Peak hours will show as bookings are completed
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
